import { useState, useEffect, useCallback } from "react";
import api from "@/lib/api";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Loader2, KeyRound, Clock, CheckCircle2, TriangleAlert } from "lucide-react";

const today = () => new Date().toISOString().slice(0, 10);

export default function VanIgnition({ canApprove }) {
  const [st, setSt] = useState(null);
  const [busy, setBusy] = useState(false);
  const [logs, setLogs] = useState([]);

  const load = useCallback(() => {
    api.get("/van-sales/ignition/status").then((r) => setSt(r.data)).catch(() => setSt({ enabled: false }));
    if (canApprove) api.get("/van-sales/ignition/logs", { params: { date: today() } }).then((r) => setLogs(r.data || [])).catch(() => {});
  }, [canApprove]);
  useEffect(() => { load(); }, [load]);

  const start = async () => {
    setBusy(true);
    try {
      let geo = null;
      if (navigator.geolocation) {
        geo = await new Promise((res) => navigator.geolocation.getCurrentPosition(
          (p) => res({ lat: p.coords.latitude, lng: p.coords.longitude }), () => res(null), { timeout: 4000 }));
      }
      const { data } = await api.post("/van-sales/ignition", { geo });
      toast.success(data.status === "on_time" ? `Zamanında! +${data.points} puan` : `${data.late_min} dk geç · ${data.points} puan`);
      load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Kaydedilemedi"); }
    finally { setBusy(false); }
  };

  const t = st?.today;
  return (
    <div className="mx-auto max-w-xl space-y-4" data-testid="van-ignition">
      <div className="rounded-xl border border-slate-200 bg-white p-5 shadow-sm">
        <div className="mb-1 flex items-center gap-2 text-sm font-semibold text-slate-800"><KeyRound className="h-4 w-4 text-brand-green" /> Mesai Başlangıcı (Kontak)</div>
        <p className="text-xs text-slate-500">Hedef saat <span className="font-semibold text-slate-700">{st?.target_time || "—"}</span> (+{st?.grace_min ?? 0} dk hoşgörü). Zamanında başlarsan puan kazanır, geç kalırsan puan kaybedersin.</p>
        {st && !st.enabled && <div className="mt-3 rounded-md bg-slate-50 p-3 text-sm text-slate-500">Kontak puanlaması kapalı.</div>}
        {st?.enabled && !t && (
          <Button className="mt-4 w-full py-6 text-base" onClick={start} disabled={busy} data-testid="ignition-start-btn">
            {busy ? <Loader2 className="mr-2 h-5 w-5 animate-spin" /> : <KeyRound className="mr-2 h-5 w-5" />} Kontağı Aç / Mesaiye Başla
          </Button>
        )}
        {t && (
          <div className={`mt-4 flex items-center gap-3 rounded-lg p-4 ${t.status === "on_time" ? "bg-emerald-50 text-emerald-800" : "bg-amber-50 text-amber-800"}`} data-testid="ignition-today">
            {t.status === "on_time" ? <CheckCircle2 className="h-6 w-6" /> : <TriangleAlert className="h-6 w-6" />}
            <div>
              <div className="font-semibold">{t.status === "on_time" ? "Zamanında başladın 👏" : `${t.late_min} dk geç başladın`}</div>
              <div className="text-xs">Kontak: {t.time} · Puan: <span className="font-bold">{t.points > 0 ? "+" : ""}{t.points}</span></div>
            </div>
          </div>
        )}
      </div>

      {canApprove && (
        <div className="overflow-x-auto rounded-xl border border-slate-200 bg-white shadow-sm" data-testid="ignition-logs">
          <div className="flex items-center gap-2 border-b border-slate-100 px-4 py-2.5 text-sm font-semibold text-slate-800"><Clock className="h-4 w-4 text-brand-green" /> Bugünkü Kontak Kayıtları</div>
          <table className="w-full text-sm">
            <thead className="bg-slate-50 text-left text-[11px] uppercase text-slate-400"><tr><th className="px-3 py-2">Şoför</th><th>Saat</th><th>Durum</th><th className="text-right">Puan</th></tr></thead>
            <tbody>
              {logs.length === 0 && <tr><td colSpan="4" className="py-6 text-center text-slate-400">Bugün kontak kaydı yok.</td></tr>}
              {logs.map((l) => (
                <tr key={l.id} className="border-t border-slate-50">
                  <td className="px-3 py-2 font-medium text-slate-800">{l.driver_name}</td>
                  <td className="text-slate-600">{l.time}</td>
                  <td>{l.status === "on_time" ? <span className="rounded bg-emerald-100 px-1.5 text-xs text-emerald-700">Zamanında</span> : <span className="rounded bg-amber-100 px-1.5 text-xs text-amber-700">{l.late_min} dk geç</span>}</td>
                  <td className={`text-right font-semibold ${l.points >= 0 ? "text-emerald-700" : "text-red-600"}`}>{l.points > 0 ? "+" : ""}{l.points}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}
